import { useEffect, useRef, useState, type ReactNode } from 'react';
import { fetchProcessingView, type FirstPassCounts, type FirstPassServiceView, type FirstPassStripView, type FirstPassTypeView, type DrainLineView, type HistoricalImportView, type ProcessingView, type ReviewSource } from '../lib/api';
import { formatDuration } from '../lib/format-duration';
import { formatShortDate } from '../lib/format-date';
import { LoadError } from '../lib/load-error';
import { labels } from '../lib/labels';
import { usePoll } from '../lib/use-poll';
import { ConditionRow } from './ConditionRow';
import { FIRST_PASS_ID, PauseEverythingControl, type PauseCounts } from './PauseEverythingControl';
import { SendToReviewPanel } from './SendToReviewPanel';
import { WhyPanel } from './WhyPanel';

/**
 * `#processing`: the landing route. Three things, in order,
 * nothing else: the work list (OPS-01), the standing drain line (OPS-01), and the historical
 * import strip — **OPS-02's addition** — rendered only while `historicalImport` is non-null (the
 * common case), and read-only throughout: this row shows the backend's own derived progress, it
 * does not compute one client-side.
 *
 * Stat-Led (`design.md` § Macrostructure families): the page opens on the *count* of conditions
 * that need a human, because that count is the only question the operator opened the console to
 * ask. Everything below it qualifies that number. When the count is zero the tally is replaced by
 * the clean-day block rather than rendering a big `0` — a zero set in 4rem reads as an error the
 * first time you see it.
 */
export function Processing({ fragment }: { fragment?: 'why' }) {
  const [view, setView] = useState<ProcessingView | null>(null);
  const [error, setError] = useState(false);
  // Bumped to force a reload after an action changes server state (e.g. Resume pipeline) —
  // the same shape `NotProcessed`/`ExtractionRules` use with their own `reload()` callback,
  // adapted for an effect that also needs to run once on mount.
  const [reloadToken, setReloadToken] = useState(0);
  // OPS-04: the Send to review guard is opened by a row, the drain line, or nowhere — one panel,
  // whichever source last asked for it (`condition-copy.ts`: "the same control, different source").
  const [reviewSource, setReviewSource] = useState<ReviewSource | null>(null);

  // A refresh never clears the screen: only a failure before the first view shows `loadFailed`.
  const hasView = useRef(false);
  // Pause polling while the Send to review panel is open, so the rows do not change under it.
  usePoll(() => setReloadToken((token) => token + 1), reviewSource === null);

  useEffect(() => {
    let cancelled = false;
    fetchProcessingView()
      .then((result) => {
        if (cancelled) return;
        hasView.current = true;
        setError(false);
        setView(result);
      })
      .catch(() => {
        if (!cancelled && !hasView.current) setError(true);
      });
    return () => {
      cancelled = true;
    };
  }, [reloadToken]);

  const reload = () => setReloadToken((token) => token + 1);
  const pausedRow = view?.workList.find((row) => row.id === 'everything-paused');

  if (error)
    return (
      <LoadError
        text={labels.processing.loadFailed}
        onRetry={() => {
          // Back to the skeleton while the retry runs; a second failure sets `error` again.
          setError(false);
          reload();
        }}
      />
    );
  if (!view) return <LoadingSkeleton />;

  return (
    <div>
      {fragment === 'why' && <WhyPanel />}
      {/* Finding 9: the Pipeline status card (Figma `2158:13995`) sits directly under the tally or
          the clean-day block, before the condition rows, so it stays near the top on a busy day.
          QA-117: the `everything-paused` row decides whether it shows Pause or Resume, and the card
          is the only place the page draws that row: it takes the row's `detail` for the unreadable
          pause flag. */}
      <WorkList
        view={view}
        onSendToReview={setReviewSource}
        card={<PauseEverythingControl paused={pausedRow !== undefined} detail={pausedRow?.detail} counts={view.firstPassStrip && pauseCounts(view.firstPassStrip)} onPaused={reload} />}
      />
      <DrainLine
        drain={view.drain}
        heading={labels.processing.drainHeading}
        empty={labels.processing.drainEmpty}
        onSendToReview={() => setReviewSource('escalation')}
      />
      <DrainLine
        drain={view.spotCheckDrain}
        heading={labels.processing.spotCheckHeading}
        empty={labels.processing.spotCheckEmpty}
        onSendToReview={() => setReviewSource('spot-check')}
      />
      {reviewSource && (
        <SendToReviewPanel
          source={reviewSource}
          onClose={() => setReviewSource(null)}
          onSent={() => {
            reload();
          }}
        />
      )}
      {view.historicalImport && <HistoricalImport view={view.historicalImport} />}
      {view.firstPassStrip && <FirstPassStrip view={view.firstPassStrip} />}
    </div>
  );
}

/**
 * The loaded page's shape, drawn in the recessive fill, so the first reply does not move the
 * layout. The label stays real text (a screen reader reads it); the bars are decoration.
 */
function LoadingSkeleton() {
  return (
    <div className="skeleton" aria-busy="true">
      <p className="notice">{labels.processing.loading}</p>
      <div aria-hidden="true">
        <span className="skeleton__bar skeleton__bar--btn skeleton__bar--gap" />
        <div className="tally">
          <span className="skeleton__bar skeleton__bar--tally" />
          <span className="skeleton__bar skeleton__bar--head" />
        </div>
        <div className="skeleton__bar skeleton__bar--row" />
        <div className="skeleton__bar skeleton__bar--row" />
        {[0, 1].map((line) => (
          <div className="panel" key={line}>
            <span className="skeleton__bar skeleton__bar--head" />
            <span className="skeleton__bar skeleton__bar--text" />
            <span className="skeleton__bar skeleton__bar--btn" />
          </div>
        ))}
        <div className="panel">
          <span className="skeleton__bar skeleton__bar--head" />
          {[0, 1, 2, 3, 4, 5, 6, 7].map((row) => (
            <span className="skeleton__bar skeleton__bar--text" key={row} />
          ))}
        </div>
      </div>
    </div>
  );
}

/** QA-128: sum the type rows (the service rows repeat them). A floor or an incomplete count reads "at least". */
function pauseCounts({ types }: FirstPassStripView): PauseCounts {
  return {
    finishing: types.reduce((sum, row) => sum + row.inProgress, 0),
    finishingIsFloor: types.some((row) => row.inProgressIsFloor || row.inProgressIsIncomplete),
    waiting: types.reduce((sum, row) => sum + row.waiting, 0),
    waitingIsFloor: types.some((row) => row.waitingIsIncomplete),
  };
}

function FirstPassStrip({ view }: { view: FirstPassStripView }) {
  return (
    <section id={FIRST_PASS_ID} tabIndex={-1} className="page-section first-pass" aria-label={labels.processing.firstPassHeading}>
      <div className="panel__head">
        <h2>{labels.processing.firstPassHeading}</h2>
      </div>
      <p className="panel__sub">{labels.processing.firstPassWindow(formatShortDate(view.windowStart), formatShortDate(view.windowEnd))}</p>
      <FirstPassTable caption={labels.processing.firstPassColumns.service} rows={view.services} name="service" />
      <FirstPassTable caption={labels.processing.firstPassColumns.type} rows={view.types} name="type" />
    </section>
  );
}

function FirstPassTable({ caption, rows, name }: { caption: string; rows: Array<FirstPassServiceView | FirstPassTypeView>; name: 'service' | 'type' }) {
  return (
    <div className="responsive-table first-pass__table">
      {/* No visible caption: it would repeat the first column head. The name is for a screen reader. */}
      <table className="grid-first-pass" aria-label={caption}>
        <thead><tr>
          <th scope="col">{name === 'service' ? labels.processing.firstPassColumns.service : labels.processing.firstPassColumns.type}</th>
          <th className="num">{labels.processing.firstPassColumns.waiting}</th>
          <th className="num">{labels.processing.firstPassColumns.inProgress}</th>
          <th className="num">{labels.processing.firstPassColumns.done}</th>
          <th>{labels.processing.firstPassColumns.ofWhich}</th>
        </tr></thead>
      <tbody>{rows.map((row) => <FirstPassRow key={name === 'service' ? (row as FirstPassServiceView).service : (row as FirstPassTypeView).type} row={row} name={name} />)}</tbody>
      </table>
    </div>
  );
}

function FirstPassRow({ row, name }: { row: FirstPassServiceView | FirstPassTypeView; name: 'service' | 'type' }) {
  const service = name === 'service' ? (row as FirstPassServiceView).service : undefined;
  const label = service
    ? labels.processing.firstPassServices[service.toLowerCase() as keyof typeof labels.processing.firstPassServices]
    : documentTypeLabel((row as FirstPassTypeView).type);
  const note = service ? labels.processing.firstPassNotes[service.toLowerCase() as keyof typeof labels.processing.firstPassNotes] : undefined;
  return <tr>
    <th scope="row">{label}{note && <small className="first-pass__note">{note}</small>}</th>
    <td className="num">{isServiceCellAvailable(service, 'waiting') ? formatWaiting(row) : '—'}</td>
    <td className="num">{isServiceCellAvailable(service, 'inProgress') ? formatInProgress(row, name) : '—'}</td>
    <td className="num">{isServiceCellAvailable(service, 'done') ? formatDone(row) : '—'}</td>
    <td className="first-pass__split">
      <ul className="first-pass__counts">
        {formatOfWhich(row, name).map((item) => (
          <li key={item}>{item}</li>
        ))}
      </ul>
    </td>
  </tr>;
}

function formatWaiting(row: FirstPassServiceView | FirstPassTypeView): string {
  return row.waitingIsIncomplete ? '—' : String(row.waiting);
}

function formatInProgress(row: FirstPassServiceView | FirstPassTypeView, name: 'service' | 'type'): string {
  if (name === 'service') return row.inProgressIsIncomplete ? '—' : String(row.inProgress);
  const typeRow = row as FirstPassTypeView;
  if (typeRow.inProgressIsIncomplete) return '—';
  if (typeRow.inProgressOldestAgeMs === null) return String(typeRow.inProgress);
  const age = formatDuration(typeRow.inProgressOldestAgeMs);
  return `${typeRow.inProgress} (${typeRow.inProgressIsFloor ? '≥ ' : ''}${labels.processing.firstPassOldest(age)})`;
}

function formatDone(row: FirstPassServiceView | FirstPassTypeView): string {
  return row.doneLastWeekIsFloor ? `≥ ${row.doneLastWeek}` : String(row.doneLastWeek);
}

function isServiceCellAvailable(
  service: FirstPassServiceView['service'] | undefined,
  column: 'waiting' | 'inProgress' | 'done',
): boolean {
  if (!service) return true;
  if (service === 'Orchestration') return column === 'waiting';
  if (service === 'Extraction') return column !== 'waiting';
  return column === 'done';
}

/** The "Of which" counts, one short item each, in one case: `Sent on: 0`, `Gate: accept 2`. */
function formatOfWhich(row: FirstPassCounts, name: 'service' | 'type'): string[] {
  const { sentOn, notProcessed } = labels.processing.firstPassColumns;
  if (name === 'service') {
    const service = (row as FirstPassServiceView).service;
    if (service === 'Extraction') return [`${notProcessed}: ${row.notProcessed}`];
    if (service === 'Validation') return [`${sentOn}: ${row.sentOn}`];
    return ['—'];
  }
  return [`${sentOn}: ${row.sentOn}`, `${notProcessed}: ${row.notProcessed}`, ...formatCounts(row)];
}

function formatCounts(row: FirstPassCounts): string[] {
  const list = (counts: Record<string, number>) =>
    Object.entries(counts).map(([key, value]) => `${key} ${value}`).join(', ') || labels.processing.firstPassNone;
  return [labels.processing.firstPassGate(list(row.gateVerdict)), labels.processing.firstPassFailure(list(row.failureClass))];
}

function documentTypeLabel(type: string): string {
  return labels.documentType[type] ?? type;
}

function HistoricalImport({ view }: { view: HistoricalImportView }) {
  const count = formatCount(view.documentsCompleted, view.isFloor);
  const total = view.corpusTotal.toLocaleString('en-US');
  const rate = `${view.documentsPerDay.toLocaleString('en-US')}/day`;
  // Clamped because the meter is a picture of a number the backend derived: a percentage that
  // arrives above 100 (a corpus total revised downward mid-run) must not paint past the track.
  const percent = Math.min(100, Math.max(0, view.percentComplete));

  return (
    <section className="panel" aria-label={labels.processing.importHeading}>
      <div className="panel__head">
        <h2>{labels.processing.importHeading}</h2>
      </div>
      {/* The console's one animation, and it animates to a value the backend reported. Not a
          `<progress>`: the accessible reading of this strip is the sentence below it, which
          carries the floor marker, the rate, and the ETA that a bare percentage would drop. */}
      <div className="meter" aria-hidden="true">
        <div className="meter__fill" style={{ width: `${percent}%` }} />
      </div>
      <p>
        {Math.round(view.percentComplete)}% ({count} of {total}) · {rate} ·{' '}
        {view.etaText ? labels.processing.importEta(view.etaText) : labels.processing.importNoEtaYet} ·{' '}
        {labels.processing.importReadOnly}
      </p>
    </section>
  );
}

function WorkList({
  view,
  onSendToReview,
  card,
}: {
  view: ProcessingView;
  onSendToReview: (source: ReviewSource) => void;
  card: ReactNode;
}) {
  // QA-117: the card draws the `everything-paused` row. It still counts in the tally, so a paused
  // page with no other condition is "1 Needs you" and not the clean day.
  const rows = view.workList.filter((row) => row.id !== 'everything-paused');
  if (view.workList.length === 0) {
    return (
      <section aria-label={labels.processing.needsYouHeading}>
        <div className="allclear">
          <p className="allclear__headline">{labels.processing.cleanDayHeadline}</p>
          <p className="allclear__detail">
            {labels.processing.cleanDaySubline(formatDocumentsToday(view))} · {labels.processing.everySourceAnswered}
          </p>
        </div>
        {card}
      </section>
    );
  }

  return (
    <section aria-label={labels.processing.needsYouHeading}>
      {/* The heading is the number. `h2` carries the label so the document outline still reads
          "Needs you" rather than a digit. */}
      <div className="tally">
        <span className="tally__n">{view.workList.length}</span>
        <h2 className="tally__label">{labels.processing.needsYouHeading}</h2>
        <p className="tally__note">{labels.processing.cleanDaySubline(formatDocumentsToday(view))}</p>
      </div>
      {card}
      {rows.length > 0 && (
        <ul className="wlist">
          {rows.map((row) => (
            <ConditionRow key={row.id} row={row} onSendToReview={onSendToReview} />
          ))}
        </ul>
      )}
    </section>
  );
}

function DrainLine({
  drain,
  heading,
  empty,
  onSendToReview,
}: {
  drain: DrainLineView;
  heading: string;
  empty: string;
  onSendToReview: () => void;
}) {
  // "When its row fires, the row replaces the line rather than sitting above it":
  // the human-review-drain row above already carries this fact.
  if (drain.replacedByRow) return null;

  return (
    <section className="panel panel--end-cell" aria-label={heading}>
      <div className="panel__head">
        <h2>{heading}</h2>
      </div>
      {drain.count === 0 ? (
        <p className="panel__sub">{empty}</p>
      ) : (
        <p>
          {formatCount(drain.count, drain.isFloor)} documents
          {drain.ageAgainstBound ? ` · oldest ${drain.ageAgainstBound}` : ''}
        </p>
      )}
      <div className="action-row end-cell">
        <button
          type="button"
          className="btn btn--guard"
          disabled={drain.control.disabled}
          title={drain.control.note}
          onClick={() => {
            if (!drain.control.disabled) onSendToReview();
          }}
        >
          {drain.control.label}
        </button>
        {drain.control.disabled && drain.control.note && (
          <span className="action-row__note">{drain.control.note}</span>
        )}
      </div>
    </section>
  );
}

function formatDocumentsToday(view: ProcessingView): string {
  return formatCount(view.documentsToday.count, view.documentsToday.isFloor);
}

function formatCount(count: number, isFloor: boolean): string {
  const formatted = count.toLocaleString('en-US');
  return isFloor ? `≥ ${formatted}` : formatted;
}
